<template>
    <Drawer ref="drawer" :loading="isLoading" :title="'Create ' + objectName" @closing="reset()">
        <div class="tw-flex tw-justify-self-center tw-gap-3">
            <Button :active="formData['type'] === 1" @click="formData['type'] = 1">Cost Department</Button>
            <Button :active="formData['type'] === 2" class="tw-ml-auto tw-block" @click="formData['type'] = 2">
                Revenue Stream
            </Button>
        </div>

        <FormField class="tw-mt-3" :label="objectName + ' Name'">
            <Textbox v-model="formData['name']" maxlength="255" expanded />
        </FormField>

        <FormField class="tw-mt-3" label="Icon">
            <Icon v-model="formData['icon']" />
        </FormField>

        <FormField class="tw-mt-3" label="Description">
            <Textbox v-model="formData['description']" maxlength="255" type="text" expanded />
        </FormField>

        <FormField
            v-if="eventContext"
            class="tw-mt-3"
            :label="'Fixed ' + (formData['type'] === 1 ? 'Costs' : 'Revenue')"
            expanded
        >
            <div class="tw-flex">
                <Button disabled>{{ currentEvent.currency_symbol }}</Button>
                <NumberInput v-model="formData['fixed']" step="0.01" :dp="2" min="0" class="tw-flex-1" />
            </div>
        </FormField>

        <FormField class="tw-mt-3" label="Default Tax Rate">
            <TaxRateSelector
                v-model="formData['default_tax_rate_id']"
                :type="formData['type'] === 2 ? 'income' : 'expense'"
            />
        </FormField>

        <FormField v-if="formData['type'] === 2" label="What best describes this Revenue Stream?" class="tw-mt-3">
            <div class="tw-grid tw-grid-cols-1 tw-gap-2 tw-mt-1">
                <RadioBox
                    v-if="currentAccount.features.ticketing"
                    :checked="formData.stream_type === 0"
                    @click="formData.stream_type = 0"
                >
                    <div>Tickets</div>
                    <small class="tw-opacity-75">Setup ticket types and sales channels, log sales and refunds.</small>
                </RadioBox>
                <RadioBox :checked="formData.stream_type === 3" @click="formData.stream_type = 3">
                    <div>Nominal Amount (General Revenue)</div>
                    <small class="tw-opacity-75">Allows you to create revenue entries of a nominal amount.</small>
                </RadioBox>
                <RadioBox :checked="formData.stream_type === 1" @click="formData.stream_type = 1">
                    <div>Revenue Per Head</div>
                    <small class="tw-opacity-75">
                        Create dynamic revenue per head configurations for your event. Revenue changes with the number
                        of tickets sold and available.
                    </small>
                </RadioBox>
                <RadioBox :checked="formData.stream_type === 2" @click="formData.stream_type = 2">
                    <div>Traders (General Revenue)</div>
                    <small class="tw-opacity-75">Allows you to create revenue entries of a nominal amount.</small>
                </RadioBox>
            </div>
        </FormField>

        <template #footer>
            <div class="tw-flex tw-justify-between">
                <Button @click="close()">Cancel</Button>
                <Button :loading="isLoading" type="is-primary" @click="save()">Create</Button>
            </div>
        </template>
    </Drawer>
</template>
<script>
import { useDataStore } from '@/js/stores/DataStore.js';
import Drawer from '@/js/components/Drawer.vue';
import TaxRateSelector from '@/js/components/controls/TaxRateSelector.vue';
import Button from '@/js/components/controls/Button.vue';
import RadioBox from '@/js/components/controls/RadioBox.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import NumberInput from '@/js/components/controls/NumberInput.vue';
import { toast } from '@/js/utils.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import Icon from '@/assets/js/eventbooks/components/utility/controls/icon.vue';

export default {
    components: {
        TaxRateSelector,
        Drawer,
        Button,
        RadioBox,
        FormField,
        Textbox,
        NumberInput,
        Icon,
    },
    data() {
        const store = useDataStore();
        return {
            isLoading: false,
            currentAccount: store.get('account'),
            currentEvent: store.get('current_event'),
            store: store,
            formData: {
                type: 1,
                fixed: 0.0,
                stream_type: null,
            },
        };
    },
    computed: {
        objectName() {
            if (this.formData.type && this.formData.type === 2) {
                return 'Revenue Stream';
            }
            return 'Cost Department';
        },
        eventContext() {
            return this.currentEvent && !this.currentEvent.is_closed;
        },
    },
    mounted() {
        Eventbus.$on('create:department', (payload) => {
            if (payload && payload.type && payload.type === 'revenue') {
                this.formData['type'] = 2;
                this.formData['stream_type'] = 3;
            } else {
                this.formData['type'] = 1;
                this.formData['stream_type'] = null;
            }

            this.open();
        });
    },
    methods: {
        open() {
            this.$refs.drawer.open();
        },
        close() {
            this.reset();
            this.$refs.drawer.close();
        },
        save() {
            this.isLoading = true;

            const uri = this.eventContext
                ? route('api.account.event.departments.create')
                : route('api.account.departments.create');

            axios.post(uri, this.formData).then(
                ({ data }) => {
                    capturePosthogEvent(
                        this.formData.type === 2 ? 'revenue_stream_created' : 'cost_department_created'
                    );
                    toast(
                        `${this.objectName} Saved`,
                        'Your changes to ' + this.formData.name + ' have been saved successfully',
                        'success'
                    );
                    Eventbus.$emit('subcategory:open', {
                        department: data.id,
                        departmentType: this.formData.type,
                    });
                    Eventbus.$emit('reload:departments');
                    this.reset();
                    this.$refs.drawer.close();
                },
                () => {
                    this.isLoading = false;
                }
            );
        },
        reset() {
            this.formData = {
                type: 1,
                fixed: 0.0,
            };
            this.isLoading = false;
        },
    },
};
</script>
